import { LIST_EXPORT_SCHEMA_V1, PLAN_PACKAGE_SCHEMA_V2 } from "@shared/types";
import { genId } from "@/components/plan/planUtils";
/** Normalize a title/text for matching: trim + collapse spaces + lowercase. */
export function normalizeTitle(s) {
    return s.trim().replace(/\s+/g, " ").toLowerCase();
}
export class ListImportError extends Error {
}
// ── Deep validation ─────────────────────────────────────────────────────────
function isObject(v) {
    return typeof v === "object" && v !== null && !Array.isArray(v);
}
function validateItem(raw, path) {
    if (!isObject(raw)) {
        throw new ListImportError(`מבנה הפריט שגוי (${path}).`);
    }
    if (typeof raw.text !== "string" || raw.text.trim() === "") {
        throw new ListImportError(`לפריט חסר טקסט תקין (${path}.text).`);
    }
    if (raw.quantity !== undefined && typeof raw.quantity !== "string") {
        throw new ListImportError(`כמות הפריט חייבת להיות טקסט (${path}.quantity).`);
    }
    if (raw.fulfillmentMode !== undefined &&
        raw.fulfillmentMode !== "normal" &&
        raw.fulfillmentMode !== "buy_there") {
        throw new ListImportError(`ערך לא חוקי לאופן השלמה (${path}.fulfillmentMode) — מותר "normal" או "buy_there".`);
    }
    const item = {
        text: raw.text,
    };
    if (typeof raw.quantity === "string")
        item.quantity = raw.quantity;
    if (raw.fulfillmentMode === "buy_there")
        item.fulfillmentMode = "buy_there";
    return item;
}
function validateSubsection(raw, path) {
    if (!isObject(raw)) {
        throw new ListImportError(`מבנה תת-הקטגוריה שגוי (${path}).`);
    }
    if (raw.title !== undefined && typeof raw.title !== "string") {
        throw new ListImportError(`כותרת תת-הקטגוריה חייבת להיות טקסט (${path}.title).`);
    }
    if (!Array.isArray(raw.items)) {
        throw new ListImportError(`תת-הקטגוריה חייבת לכלול רשימת פריטים (${path}.items).`);
    }
    const items = raw.items.map((item, i) => validateItem(item, `${path}.items[${i}]`));
    const sub = { items };
    if (typeof raw.title === "string" && raw.title.trim() !== "")
        sub.title = raw.title;
    return sub;
}
function validateSection(raw, path) {
    if (!isObject(raw)) {
        throw new ListImportError(`מבנה הקטגוריה שגוי (${path}).`);
    }
    if (typeof raw.title !== "string" || raw.title.trim() === "") {
        throw new ListImportError(`לקטגוריה חסרה כותרת תקינה (${path}.title).`);
    }
    if (raw.icon !== undefined && typeof raw.icon !== "string") {
        throw new ListImportError(`אייקון הקטגוריה חייב להיות טקסט (${path}.icon).`);
    }
    if (raw.sectionKind !== undefined &&
        raw.sectionKind !== "normal" &&
        raw.sectionKind !== "shopping" &&
        raw.sectionKind !== "family_checklist") {
        throw new ListImportError(`ערך לא חוקי לסוג הקטגוריה (${path}.sectionKind) — מותר "normal", "shopping" או "family_checklist".`);
    }
    if (!Array.isArray(raw.subsections)) {
        throw new ListImportError(`לקטגוריה חסרות תת-קטגוריות (${path}.subsections).`);
    }
    const subsections = raw.subsections.map((sub, i) => validateSubsection(sub, `${path}.subsections[${i}]`));
    const section = { title: raw.title, subsections };
    if (typeof raw.icon === "string")
        section.icon = raw.icon;
    if (raw.sectionKind === "shopping" || raw.sectionKind === "family_checklist") {
        section.sectionKind = raw.sectionKind;
    }
    return section;
}
/**
 * Validate unknown parsed JSON as a supported export and normalize it into a
 * single internal shape. Supports both `nodeone-list-export-v1` and
 * `nodeone-plan-package-v2`. Throws {@link ListImportError} with a
 * Hebrew-friendly, path-annotated message on any structural problem.
 */
export function parseImportData(data) {
    if (!isObject(data)) {
        throw new ListImportError("הקובץ אינו תקין — צריך להיות אובייקט JSON.");
    }
    let kind;
    if (data.schema === LIST_EXPORT_SCHEMA_V1) {
        kind = "list";
    }
    else if (data.schema === PLAN_PACKAGE_SCHEMA_V2) {
        if (data.kind !== "list" && data.kind !== "template") {
            throw new ListImportError('ערך לא חוקי לסוג החבילה (kind) — מותר "list" או "template".');
        }
        kind = data.kind;
    }
    else {
        throw new ListImportError("סכמת הקובץ אינה נתמכת (נדרש nodeone-list-export-v1 או nodeone-plan-package-v2).");
    }
    if (!Array.isArray(data.sections)) {
        throw new ListImportError("הקובץ אינו מכיל רשימת קטגוריות (sections).");
    }
    const title = typeof data.title === "string" ? data.title : "";
    const sections = data.sections.map((section, i) => validateSection(section, `sections[${i}]`));
    return { kind, title, sections };
}
/** Strip a leading UTF-8 BOM, parse JSON, then validate/normalize. */
export function parseImportText(text) {
    const clean = text.replace(/^\uFEFF/, "");
    let json;
    try {
        json = JSON.parse(clean);
    }
    catch {
        throw new ListImportError("לא ניתן לפענח את הקובץ — JSON לא תקין.");
    }
    return parseImportData(json);
}
function makeItem(text, quantity, fulfillmentMode) {
    const item = { id: genId(), text, checked: false };
    if (quantity)
        item.quantity = quantity;
    if (fulfillmentMode && fulfillmentMode !== "normal")
        item.fulfillmentMode = fulfillmentMode;
    return item;
}
/**
 * Core merge: matches categories/subsections by normalized title and items by
 * normalized text within their subsection, creating what's missing and skipping
 * duplicates (unless `includeDuplicates`). Returns the merged content plus a
 * preview of exactly what was created/added/skipped. Never replaces existing
 * content and never touches people/assignment/completion state.
 */
export function planListImport(content, data, { includeDuplicates = false } = {}) {
    const preview = {
        categoriesToCreate: [],
        subsectionsToCreate: [],
        itemsToAdd: [],
        duplicatesToSkip: [],
        duplicatesWithDifferences: [],
    };
    // Work on shallow-cloned sections/subsections/items so the original content is
    // never mutated.
    const sections = content.sections.map((s) => ({
        ...s,
        subsections: s.subsections.map((sub) => ({ ...sub, items: [...sub.items] })),
    }));
    for (const impSection of data.sections) {
        const sectionKey = normalizeTitle(impSection.title);
        let section = sections.find((s) => normalizeTitle(s.title) === sectionKey);
        if (!section) {
            section = {
                id: genId(),
                title: impSection.title,
                ...(impSection.icon ? { icon: impSection.icon } : {}),
                ...(impSection.sectionKind && impSection.sectionKind !== "normal"
                    ? { sectionKind: impSection.sectionKind }
                    : {}),
                subsections: [],
            };
            sections.push(section);
            preview.categoriesToCreate.push(impSection.title);
        }
        for (const impSub of impSection.subsections) {
            const subKey = normalizeTitle(impSub.title ?? "");
            let sub = section.subsections.find((s) => normalizeTitle(s.title ?? "") === subKey);
            if (!sub) {
                sub = {
                    id: genId(),
                    ...(impSub.title ? { title: impSub.title } : {}),
                    items: [],
                };
                section.subsections.push(sub);
                preview.subsectionsToCreate.push({
                    sectionTitle: impSection.title,
                    subTitle: impSub.title,
                });
            }
            for (const impItem of impSub.items) {
                const text = impItem.text?.trim();
                if (!text)
                    continue;
                const ref = {
                    sectionTitle: impSection.title,
                    subTitle: impSub.title,
                    text,
                };
                const itemKey = normalizeTitle(text);
                const existing = sub.items.find((i) => normalizeTitle(i.text) === itemKey);
                if (existing && !includeDuplicates) {
                    preview.duplicatesToSkip.push(ref);
                    const existingMode = existing.fulfillmentMode ?? "normal";
                    const incomingMode = impItem.fulfillmentMode ?? "normal";
                    const existingQty = existing.quantity ?? "";
                    const incomingQty = impItem.quantity ?? "";
                    if (existingMode !== incomingMode || existingQty !== incomingQty) {
                        preview.duplicatesWithDifferences.push(ref);
                    }
                    continue;
                }
                sub.items.push(makeItem(text, impItem.quantity, impItem.fulfillmentMode));
                preview.itemsToAdd.push(ref);
            }
        }
    }
    return { content: { sections }, preview };
}
export function buildImportPreview(content, data, options = {}) {
    return planListImport(content, data, options).preview;
}
export function applyListImport(content, data, options = {}) {
    return planListImport(content, data, options).content;
}
